import apiService from './apiService';
import { GET, POST, PATCH, DELETE } from './executer';
import type { EventSource } from './eventsService';

// Types for Daily Forecast
export interface DailyForecastParameter {
  name: string;
  value: string | null;
  modified: number | null;
}

export interface DailyForecast {
  forecast_date: string;
  weather_description: string | null;
  weather_description_modified: number | null;
  parameters: DailyForecastParameter[];
}

export interface ForecastDaily {
  season_year: number;
  circuit_id: string;
  start_date: string;
  general_situation: string | null;
  general_situation_modified: number | null;
  daily_forecasts: DailyForecast[];
  create_datetime: string;
  value_date: string;
  deleted?: boolean;
  updated_by?: string | null;
  update_datetime?: string | null;
  status?: 'draft' | 'published' | null;
}

export interface ForecastDailyResponse {
  success: boolean;
  season_year: number;
  circuit_id: string;
  start_date: string;
  forecasts: ForecastDaily[];
  forecast_count: number;
  total_daily_forecasts: number;
  source: string;
}

// Types for Hourly Forecast
export interface HourlyForecastParameter {
  name: string;
  value: string | null;
  modified: number | null;
}

export interface HourlyForecast {
  forecast_datetime: string;
  parameters: HourlyForecastParameter[];
}

/**
 * Per-day metadata for weather conditions
 * Each day of the event can have its own weather summary, alert level, and previous night description
 */
export interface DailyMetadata {
  date: string; // YYYY-MM-DD format
  weather_summary: string | null;
  weather_summary_modified: number | null;
  alert_level: string | null;
  alert_level_modified: number | null;
  previous_night: string | null;
  previous_night_modified: number | null;
}

export interface ForecastHourly {
  season_year: number;
  circuit_id: string;
  start_date: string;
  // Root-level fields kept for backward compatibility with legacy data
  weather_summary: string | null;
  weather_summary_modified: number | null;
  alert_level: string | null;
  alert_level_modified: number | null;
  previous_night: string | null;
  previous_night_modified: number | null;
  // Per-day metadata array - use this when available for day-specific data
  daily_metadata?: DailyMetadata[];
  hourly_forecasts: HourlyForecast[];
  create_datetime: string;
  value_date: string;
  deleted?: boolean;
  updated_by?: string | null;
  update_datetime?: string | null;
  status?: 'draft' | 'published' | null;
}

export interface ForecastHourlyResponse {
  success: boolean;
  season_year: number;
  circuit_id: string;
  start_date: string;
  forecasts: ForecastHourly[];
  forecast_count: number;
  total_hourly_forecasts: number;
  source: string;
}

// Types for Rain Forecast
export interface ForecastRain {
  season_year: number;
  circuit_id: string;
  start_date: string;
  session_type: string | null;
  created_at: string;
  value_date: string;
  is_active: boolean;
  message: string;
  rain_type: number | null;
  few_drops: boolean | null;
  rain_speed_kph: number | null;
  rain_direction_deg: number | null;
  rain_intensity: number | null;
  rain_pattern: string | null;
  rain_start_time: string | null;
  rain_end_time: string | null;
  was_modified: boolean | null;
  deleted?: boolean;
  updated_by?: string | null;
  update_datetime?: string | null;
}

export interface ForecastRainResponse {
  success: boolean;
  season_year: number;
  circuit_id: string;
  start_date: string;
  forecasts: ForecastRain[];
  forecast_count: number;
  source: string;
}

// Types for Minutely Forecast
export interface MinutelyForecastParameter {
  name: string;
  value: string | null;
  modified: number | null;
}

export interface MinutelyForecast {
  forecast_datetime: string;
  parameters: MinutelyForecastParameter[];
}

export interface ForecastMinutely {
  season_year: number;
  circuit_id: string;
  start_date: string;
  minutely_forecasts: MinutelyForecast[];
  create_datetime: string;
  value_date: string;
  deleted?: boolean;
}

export interface ForecastMinutelyResponse {
  success: boolean;
  season_year: number;
  circuit_id: string;
  start_date: string;
  forecasts: ForecastMinutely[];
  forecast_count: number;
  total_minutely_forecasts: number;
  source: string;
}

interface GetForecastParams {
  season_year: number;
  circuit_id: string;
  start_date: string;
  source?: EventSource;
  start_time?: string;
  end_time?: string;
  include_deleted?: boolean;
}

export interface CreateForecastRainRequest {
  season_year: number;
  circuit_id: string;
  start_date: string;
  session_type: string;
  value_date: string;
  is_active: boolean;
  message: string;
  rain_type: number | null;
  few_drops: boolean | null;
  rain_speed_kph: number | null;
  rain_direction_deg: number | null;
  rain_intensity: number | null;
  rain_pattern: string | null;
  rain_start_time: string | null;
  rain_end_time: string | null;
  demo?: boolean;
}

export interface CreateForecastRainResponse {
  success: boolean;
  message: string;
  forecast?: ForecastRain;
}

class ForecastService {
  /**
   * Get daily forecast data for an event
   */
  async getForecastDaily(params: GetForecastParams): Promise<ForecastDailyResponse> {
    const { season_year, circuit_id, start_date, source = 'prod', start_time, end_time, include_deleted } = params;
    
    const queryParams = new URLSearchParams({
      source,
    });

    if (start_time) {
      queryParams.set('start_time', start_time);
    }

    if (end_time) {
      queryParams.set('end_time', end_time);
    }

    if (include_deleted) {
      queryParams.set('include_deleted', 'true');
    }

    const response = await apiService.client.execute<ForecastDailyResponse>(
      GET,
      `/api/forecast-daily/${season_year}/${circuit_id}/${start_date}?${queryParams.toString()}`
    );

    if (!response.success || !response.data) {
      throw new Error('Failed to fetch daily forecasts');
    }

    return response.data;
  }

  /**
   * Get hourly forecast data for an event
   */
  async getForecastHourly(params: GetForecastParams): Promise<ForecastHourlyResponse> {
    const { season_year, circuit_id, start_date, source = 'prod', start_time, end_time, include_deleted } = params;
    
    const queryParams = new URLSearchParams({
      source,
    });

    if (start_time) {
      queryParams.set('start_time', start_time);
    }

    if (end_time) {
      queryParams.set('end_time', end_time);
    }

    if (include_deleted) {
      queryParams.set('include_deleted', 'true');
    }

    const response = await apiService.client.execute<ForecastHourlyResponse>(
      GET,
      `/api/forecast-hourly/${season_year}/${circuit_id}/${start_date}?${queryParams.toString()}`
    );

    if (!response.success || !response.data) {
      throw new Error('Failed to fetch hourly forecasts');
    }

    return response.data;
  }

  /**
   * Get rain forecast data for an event
   */
  async getForecastRain(params: GetForecastParams): Promise<ForecastRainResponse> {
    const { season_year, circuit_id, start_date, source = 'prod', start_time, end_time, include_deleted } = params;
    
    const queryParams = new URLSearchParams({
      source,
    });

    if (start_time) {
      queryParams.set('start_time', start_time);
    }

    if (end_time) {
      queryParams.set('end_time', end_time);
    }

    if (include_deleted) {
      queryParams.set('include_deleted', 'true');
    }

    const response = await apiService.client.execute<ForecastRainResponse>(
      GET,
      `/api/forecast-rain/${season_year}/${circuit_id}/${start_date}?${queryParams.toString()}`
    );

    if (!response.success || !response.data) {
      throw new Error('Failed to fetch rain forecasts');
    }

    return response.data;
  }

  /**
   * Get minutely forecast data for an event
   */
  async getForecastMinutely(params: GetForecastParams): Promise<ForecastMinutelyResponse> {
    const { season_year, circuit_id, start_date, source = 'prod', start_time, end_time, include_deleted } = params;
    
    const queryParams = new URLSearchParams({
      source,
    });

    if (start_time) {
      queryParams.set('start_time', start_time);
    }

    if (end_time) {
      queryParams.set('end_time', end_time);
    }

    if (include_deleted) {
      queryParams.set('include_deleted', 'true');
    }

    const response = await apiService.client.execute<ForecastMinutelyResponse>(
      GET,
      `/api/forecast-minutely/${season_year}/${circuit_id}/${start_date}?${queryParams.toString()}`
    );

    if (!response.success || !response.data) {
      throw new Error('Failed to fetch minutely forecasts');
    }

    return response.data;
  }

  /**
   * Create a new rain forecast
   */
  async createForecastRain(data: CreateForecastRainRequest): Promise<CreateForecastRainResponse> {
    const response = await apiService.client.execute<CreateForecastRainResponse>(
      POST,
      '/api/forecast-rain',
      data
    );

    if (!response.success || !response.data) {
      throw new Error(response.errorData?.message || 'Failed to create rain forecast');
    }

    return response.data;
  }

  async updateForecastRain(params: {
    season_year: number;
    circuit_id: string;
    start_date: string;
    value_date: string;
    created_at: string;
    session_type?: string;
    is_active?: boolean;
    message?: string;
    rain_type?: number | null;
    few_drops?: boolean | null;
    rain_speed_kph?: number | null;
    rain_direction_deg?: number | null;
    rain_intensity?: number | null;
    rain_pattern?: string | null;
    rain_start_time?: string | null;
    rain_end_time?: string | null;
    new_value_date?: string;
    demo?: boolean;
  }): Promise<{ success: boolean; message: string }> {
    const { season_year, circuit_id, start_date, ...body } = params;
    const response = await apiService.client.execute<{ success: boolean; message: string }>(
      PATCH,
      `/api/forecast-rain/${season_year}/${circuit_id}/${start_date}`,
      body
    );
    if (!response.success || !response.data) {
      throw new Error(response.errorData?.message || 'Failed to update rain forecast');
    }
    return response.data;
  }

  async deleteForecastRain(params: {
    season_year: number;
    circuit_id: string;
    start_date: string;
    value_date: string;
    created_at: string;
    source?: EventSource;
  }): Promise<{ success: boolean; message: string }> {
    const { season_year, circuit_id, start_date, value_date, created_at, source = 'prod' } = params;
    const queryParams = new URLSearchParams({ value_date, created_at });
    if (source !== 'prod') {
      queryParams.set('source', source);
    }
    const response = await apiService.client.execute<{ success: boolean; message: string }>(
      DELETE,
      `/api/forecast-rain/${season_year}/${circuit_id}/${start_date}?${queryParams.toString()}`
    );
    if (!response.success || !response.data) {
      throw new Error(response.errorData?.message || 'Failed to delete rain forecast');
    }
    return response.data;
  }

  async setForecastRainDeleted(params: {
    season_year: number;
    circuit_id: string;
    start_date: string;
    value_date: string;
    created_at: string;
    deleted: boolean;
    demo?: boolean;
  }): Promise<{ success: boolean; message: string }> {
    const { season_year, circuit_id, start_date, ...body } = params;
    const response = await apiService.client.execute<{ success: boolean; message: string }>(
      PATCH,
      `/api/forecast-rain/${season_year}/${circuit_id}/${start_date}/deleted`,
      body
    );
    if (!response.success || !response.data) {
      throw new Error(response.errorData?.message || 'Failed to update rain forecast deleted flag');
    }
    return response.data;
  }

  /**
   * Update daily forecast text fields (general_situation, weather_description)
   * Requires root user authentication
   */
  async updateForecastDailyText(params: {
    season_year: number;
    circuit_id: string;
    start_date: string;
    value_date: string;
    general_situation?: string;
    forecast_date?: string;
    weather_description?: string;
    source?: EventSource;
  }): Promise<{ success: boolean; message: string }> {
    const { season_year, circuit_id, start_date, source = 'prod', ...body } = params;
    
    const queryParams = new URLSearchParams();
    if (source !== 'prod') {
      queryParams.set('source', source);
    }
    const queryString = queryParams.toString();
    const url = `/api/forecast-daily/${season_year}/${circuit_id}/${start_date}/text${queryString ? `?${queryString}` : ''}`;

    const response = await apiService.client.execute<{ success: boolean; message: string }>(
      PATCH,
      url,
      body
    );

    if (!response.success || !response.data) {
      throw new Error(response.errorData?.message || 'Failed to update daily forecast text');
    }

    return response.data;
  }

  /**
   * Fetch a single daily forecast by value_date and create_datetime (same params as delete)
   * Used to verify before delete. Returns exactly one forecast or throws.
   */
  async verifyForecastDaily(params: {
    season_year: number;
    circuit_id: string;
    start_date: string;
    value_date: string;
    create_datetime: string;
    source?: EventSource;
  }): Promise<{ success: boolean; forecast: ForecastDaily }> {
    const { season_year, circuit_id, start_date, value_date, create_datetime, source = 'prod' } = params;
    const queryParams = new URLSearchParams({ value_date, create_datetime });
    if (source !== 'prod') {
      queryParams.set('source', source);
    }
    const response = await apiService.client.execute<{ success: boolean; forecast: ForecastDaily }>(
      GET,
      `/api/forecast-daily/${season_year}/${circuit_id}/${start_date}/verify?${queryParams.toString()}`
    );
    if (!response.success || !response.data?.forecast) {
      throw new Error(response.errorData?.message || 'Failed to verify daily forecast');
    }
    return response.data;
  }

  /**
   * Fetch a single hourly forecast by value_date and create_datetime (same params as delete)
   * Used to verify before delete. Returns exactly one forecast or throws.
   */
  async verifyForecastHourly(params: {
    season_year: number;
    circuit_id: string;
    start_date: string;
    value_date: string;
    create_datetime: string;
    source?: EventSource;
  }): Promise<{ success: boolean; forecast: ForecastHourly }> {
    const { season_year, circuit_id, start_date, value_date, create_datetime, source = 'prod' } = params;
    const queryParams = new URLSearchParams({ value_date, create_datetime });
    if (source !== 'prod') {
      queryParams.set('source', source);
    }
    const response = await apiService.client.execute<{ success: boolean; forecast: ForecastHourly }>(
      GET,
      `/api/forecast-hourly/${season_year}/${circuit_id}/${start_date}/verify?${queryParams.toString()}`
    );
    if (!response.success || !response.data?.forecast) {
      throw new Error(response.errorData?.message || 'Failed to verify hourly forecast');
    }
    return response.data;
  }

  /**
   * Delete a daily forecast by value_date and create_datetime
   * Requires root user authentication
   */
  async deleteForecastDaily(params: {
    season_year: number;
    circuit_id: string;
    start_date: string;
    value_date: string;
    create_datetime: string;
    source?: EventSource;
  }): Promise<{ success: boolean; message: string }> {
    const { season_year, circuit_id, start_date, value_date, create_datetime, source = 'prod' } = params;
    const queryParams = new URLSearchParams({ value_date, create_datetime });
    if (source !== 'prod') {
      queryParams.set('source', source);
    }
    const response = await apiService.client.execute<{ success: boolean; message: string }>(
      DELETE,
      `/api/forecast-daily/${season_year}/${circuit_id}/${start_date}?${queryParams.toString()}`
    );
    if (!response.success || !response.data) {
      throw new Error(response.errorData?.message || 'Failed to delete daily forecast');
    }
    return response.data;
  }

  /**
   * Delete an hourly forecast by value_date and create_datetime
   * Requires root user authentication
   */
  async deleteForecastHourly(params: {
    season_year: number;
    circuit_id: string;
    start_date: string;
    value_date: string;
    create_datetime: string;
    source?: EventSource;
  }): Promise<{ success: boolean; message: string }> {
    const { season_year, circuit_id, start_date, value_date, create_datetime, source = 'prod' } = params;
    const queryParams = new URLSearchParams({ value_date, create_datetime });
    if (source !== 'prod') {
      queryParams.set('source', source);
    }
    const response = await apiService.client.execute<{ success: boolean; message: string }>(
      DELETE,
      `/api/forecast-hourly/${season_year}/${circuit_id}/${start_date}?${queryParams.toString()}`
    );
    if (!response.success || !response.data) {
      throw new Error(response.errorData?.message || 'Failed to delete hourly forecast');
    }
    return response.data;
  }

  /**
   * Set the soft-delete flag on a daily or hourly forecast (for restore or soft-delete)
   * Requires root user authentication
   */
  async setForecastDeleted(
    type: 'daily' | 'hourly',
    params: {
      season_year: number;
      circuit_id: string;
      start_date: string;
      value_date: string;
      create_datetime: string;
      deleted: boolean;
      source?: EventSource;
    }
  ): Promise<{ success: boolean; message: string }> {
    const { season_year, circuit_id, start_date, value_date, create_datetime, deleted, source = 'prod' } = params;
    const queryParams = new URLSearchParams();
    if (source !== 'prod') {
      queryParams.set('source', source);
    }
    const path = type === 'daily' ? 'forecast-daily' : 'forecast-hourly';
    const url = `/api/${path}/${season_year}/${circuit_id}/${start_date}/deleted${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiService.client.execute<{ success: boolean; message: string }>(
      PATCH,
      url,
      { value_date, create_datetime, deleted }
    );
    if (!response.success || !response.data) {
      throw new Error(response.errorData?.message || 'Failed to update forecast deleted flag');
    }
    return response.data;
  }

  /**
   * Update hourly forecast text fields (previous_night, weather_summary)
   * Requires root user authentication.
   * When date (YYYY-MM-DD) is provided, persists to daily_metadata for that day (required for forecasts with per-day metadata).
   */
  async updateForecastHourlyText(params: {
    season_year: number;
    circuit_id: string;
    start_date: string;
    value_date: string;
    previous_night?: string;
    weather_summary?: string;
    date?: string; // YYYY-MM-DD - when provided, updates daily_metadata for this day
    source?: EventSource;
  }): Promise<{ success: boolean; message: string }> {
    const { season_year, circuit_id, start_date, source = 'prod', ...body } = params;
    
    const queryParams = new URLSearchParams();
    if (source !== 'prod') {
      queryParams.set('source', source);
    }
    const queryString = queryParams.toString();
    const url = `/api/forecast-hourly/${season_year}/${circuit_id}/${start_date}/text${queryString ? `?${queryString}` : ''}`;

    const response = await apiService.client.execute<{ success: boolean; message: string }>(
      PATCH,
      url,
      body
    );

    if (!response.success || !response.data) {
      throw new Error(response.errorData?.message || 'Failed to update hourly forecast text');
    }

    return response.data;
  }

  /**
   * Update the publication status of a daily or hourly forecast (draft/published).
   * Requires root user authentication.
   */
  async updateForecastStatus(
    type: 'daily' | 'hourly',
    params: {
      season_year: number;
      circuit_id: string;
      start_date: string;
      value_date: string;
      create_datetime: string;
      status: 'draft' | 'published';
      source?: EventSource;
    }
  ): Promise<{ success: boolean; message: string }> {
    const { season_year, circuit_id, start_date, value_date, create_datetime, status, source = 'prod' } = params;
    const queryParams = new URLSearchParams();
    if (source !== 'prod') {
      queryParams.set('source', source);
    }
    const path = type === 'daily' ? 'forecast-daily' : 'forecast-hourly';
    const url = `/api/${path}/${season_year}/${circuit_id}/${start_date}/status${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiService.client.execute<{ success: boolean; message: string }>(
      PATCH,
      url,
      { value_date, create_datetime, status }
    );
    if (!response.success || !response.data) {
      throw new Error(response.errorData?.message || 'Failed to update forecast status');
    }
    return response.data;
  }

  /**
   * Update individual weather parameter values in hourly forecasts.
   * Requires root user authentication.
   */
  async updateForecastHourlyParameters(params: {
    season_year: number;
    circuit_id: string;
    start_date: string;
    value_date: string;
    updates: Array<{ forecast_datetime: string; name: string; value: string }>;
    source?: EventSource;
  }): Promise<{
    season_year: number;
    circuit_id: string;
    start_date: string;
    value_date: string;
    updates_count: number;
    applied_updates: Array<{ forecast_datetime: string; name: string; value: string }>;
  }> {
    const { season_year, circuit_id, start_date, source = 'prod', ...body } = params;

    const queryParams = new URLSearchParams();
    if (source !== 'prod') {
      queryParams.set('source', source);
    }
    const queryString = queryParams.toString();
    const url = `/api/forecast-hourly/${season_year}/${circuit_id}/${start_date}/parameters${queryString ? `?${queryString}` : ''}`;

    const response = await apiService.client.execute<{
      season_year: number;
      circuit_id: string;
      start_date: string;
      value_date: string;
      updates_count: number;
      applied_updates: Array<{ forecast_datetime: string; name: string; value: string }>;
    }>(
      PATCH,
      url,
      body
    );

    if (!response.success || !response.data) {
      throw new Error(response.errorData?.message || 'Failed to update hourly forecast parameters');
    }

    return response.data;
  }

  /**
   * Update individual weather parameter values in daily forecasts.
   * Requires root user authentication.
   */
  async updateForecastDailyParameters(params: {
    season_year: number;
    circuit_id: string;
    start_date: string;
    value_date: string;
    updates: Array<{ forecast_date: string; name: string; value: string }>;
    source?: EventSource;
  }): Promise<{
    season_year: number;
    circuit_id: string;
    start_date: string;
    value_date: string;
    updates_count: number;
    applied_updates: Array<{ forecast_date: string; name: string; value: string }>;
  }> {
    const { season_year, circuit_id, start_date, source = 'prod', ...body } = params;

    const queryParams = new URLSearchParams();
    if (source !== 'prod') {
      queryParams.set('source', source);
    }
    const queryString = queryParams.toString();
    const url = `/api/forecast-daily/${season_year}/${circuit_id}/${start_date}/parameters${queryString ? `?${queryString}` : ''}`;

    const response = await apiService.client.execute<{
      season_year: number;
      circuit_id: string;
      start_date: string;
      value_date: string;
      updates_count: number;
      applied_updates: Array<{ forecast_date: string; name: string; value: string }>;
    }>(
      PATCH,
      url,
      body
    );

    if (!response.success || !response.data) {
      throw new Error(response.errorData?.message || 'Failed to update daily forecast parameters');
    }

    return response.data;
  }
}

const forecastService = new ForecastService();
export default forecastService;

